import convertUrlfromS3toCloudFront from './convertUrlfromS3toCloudFront';
import { withDebugQuery } from './debugLog';
import { isUrlValid } from './regex';

const PRODUCTION_VIDEO_PLAYER_ORIGIN = 'https://logictry.com';

function isCdnLogictryPlaybackUrl(url) {
  try {
    return new URL(url).hostname.toLowerCase() === 'cdn.logictry.com';
  } catch {
    return false;
  }
}

/**
 * CDN m3u8 (`cdn.logictry.com`) hotlink guard requires a `*.logictry.com` referer — always use production
 * videoplayer (including localhost dev). IVS live has no referer guard but also uses production so local dev
 * does not need a `/videoplayer` route. Same-origin localhost videoplayer is only for non-CDN assets.
 */
function logictryVideoPlayerOrigin({ ivsLive = false, cdnPlayback = false } = {}) {
  if (ivsLive || cdnPlayback) return PRODUCTION_VIDEO_PLAYER_ORIGIN;
  if (typeof window !== 'undefined') {
    const { hostname } = window.location;
    if (hostname === 'localhost' || hostname.endsWith('.localhost')) {
      return window.location.origin;
    }
  }
  return PRODUCTION_VIDEO_PLAYER_ORIGIN;
}

const VIDEO_UPLOAD_PATH_RE = /\.(mp4|mov|webm|m4v|ogv)(\?|#|$)/i;
const HLS_MANIFEST_PATH_RE = /\.m3u8(\?|#|$)/i;

/** @returns {boolean} */
function pathnameLooksLikeUploadedVideoAsset(urlString) {
  if (!urlString || typeof urlString !== 'string') return false;
  try {
    return VIDEO_UPLOAD_PATH_RE.test(new URL(urlString).pathname || '');
  } catch {
    return false;
  }
}

/** CDN HLS manifest (upload transcodes or live replay copies). */
function pathnameLooksLikeLogictryHlsPlayback(urlString) {
  if (!urlString || typeof urlString !== 'string') return false;
  try {
    const parsed = new URL(urlString);
    return HLS_MANIFEST_PATH_RE.test(parsed.pathname || '')
      && parsed.hostname.toLowerCase() === 'cdn.logictry.com';
  } catch {
    return false;
  }
}

/**
 * True when this URL looks like Logictry-hosted user uploads (userfiles S3/CDN path) ending in video extension.
 *
 * `isLogictryUserfilesS3` — truthy `checkContentType(...).isS3Url`.
 *
 * @param {string | null} urlString
 * @param {unknown} isLogictryUserfilesS3
 */
export function isLogictryUserUploadVideoPlaybackUrl(urlString, isLogictryUserfilesS3) {
  if (pathnameLooksLikeLogictryHlsPlayback(urlString)) return true;
  if (!pathnameLooksLikeUploadedVideoAsset(urlString)) return false;
  if (isLogictryUserfilesS3) return true;
  try {
    return new URL(urlString).hostname.toLowerCase() === 'cdn.logictry.com';
  } catch {
    return false;
  }
}

/** Extensions whose user-upload objects are delivered as HLS at the same basename + `.m3u8`. */
const HLS_PLAYBACK_EXTENSIONS = /\.(mov|mp4|m4v)$/i;

/** Same host rewrite as thumbnails; progressive uploads → `.m3u8` for HLS. Shared with Assets preview `<video src>`. */
export function logictryUploadedVideoToCdnPlaybackUrl(originUrl) {
  let url = convertUrlfromS3toCloudFront(originUrl);
  try {
    const parsed = new URL(url);
    const { pathname } = parsed;
    if (HLS_PLAYBACK_EXTENSIONS.test(pathname)) {
      parsed.pathname = `${pathname.replace(HLS_PLAYBACK_EXTENSIONS, '')}.m3u8`;
      url = parsed.toString();
    }
  } catch {
    //
  }
  return url;
}

/**
 * `iframe.src` for content-source video: Logictry videoplayer + CDN URL (not raw S3).
 *
 * @param {string} absoluteUrl
 * @returns {string}
 */
function isIvsHlsPlaybackUrl(url) {
  return /playback\.live-video\.net/i.test(url);
}

export function logictryUploadedVideoIframeUrl(absoluteUrl, opts = {}) {
  const playback = logictryUploadedVideoToCdnPlaybackUrl(absoluteUrl);
  const ivsLive = isIvsHlsPlaybackUrl(playback);
  const cdnPlayback = isCdnLogictryPlaybackUrl(playback);
  const hasM3u8 = /\.m3u8(\?|#|$)/i.test(playback) || ivsLive;
  const playerOrigin = logictryVideoPlayerOrigin({ ivsLive, cdnPlayback });
  const base = `${playerOrigin}/videoplayer?video=${encodeURIComponent(playback)}`;
  let url = hasM3u8 ? `${base}&type=m3u8` : base;
  if (opts.autoplay) url += '&autoplay=1';
  if (opts.live || ivsLive) url += '&live=1';
  // Parent owns the control chrome via postMessage (livestream replay/live).
  // noplayui: the parent draws the center play — hide the iframe's own big-play
  // (older deployed players ignore the flag; the parent button covers theirs).
  if (opts.remote) url += '&remote=1&noplayui=1';
  // Start muted so autoplay is allowed (browsers block audible autoplay).
  if (opts.startMuted) url += '&startmuted=1';
  if (opts.cover) url += '&cover=1';
  if (opts.loop) url += '&loop=1';
  // Frame picker: CORS-mode <video> so canvas frame capture is not tainted.
  if (opts.capture) url += '&capture=1';
  // Cross-origin /videoplayer cannot read parent localStorage — propagate gate.
  return withDebugQuery(url);
}

const STORY_VIDEO_SOURCE_RE = /\.(mp4|mov|webm|m4v|ogv)(\?|#|$)/i;

/** LogicBase stories: stored S3 URL → CDN MP4 (short clips; no HLS transcode wait). */
export function getLogicBaseStoryDisplayMedia(storyUrl) {
  if (!storyUrl) return { isVideo: false, url: '' };
  const isVideo = STORY_VIDEO_SOURCE_RE.test(storyUrl);
  if (isVideo) {
    return {
      isVideo: true,
      url: convertUrlfromS3toCloudFront(storyUrl) || storyUrl,
    };
  }
  return {
    isVideo: false,
    url: convertUrlfromS3toCloudFront(storyUrl) || storyUrl,
  };
}

export default function checkContentType(customUrl) {
  const isS3Url = validateS3Url(customUrl);
  const isImage = isImageUrl(customUrl);
  const googleUrl = convertToGooglePreviewOrMobileBasicUrl(customUrl);
  const googleDriveUrl = convertGoogleDriveUrl(customUrl);
  const youtubeUrl = convertToYouTubeEmbedUrl(customUrl);
  const loomUrl = convertToLoomEmbedUrl(customUrl);
  const airtableUrl = validateAirtableUrl(customUrl);
  const googleMapsUrl = convertToGoogleMapsEmbed(customUrl);
  const canvaUrl = convertToCanvaEmbedUrl(customUrl);
  const googleCalendarUrl = convertToGoogleCalendarEmbedUrl(customUrl);
  const vimeoUrl = convertVimeoToEmbed(customUrl);
  const instagramEmbedCode = convertInstagramReelToEmbedUrl(customUrl);
  const tiktokEmbedCode = convertTikTokToEmbedUrl(customUrl);
  const isPDF = isPdfUrl(customUrl);
  const isLogictryUrl = validateLogictryUrl(customUrl);
  const videoUrl = validateVideoUrl(customUrl);
  const otherAcceptedUrls = otherAcceptedSitesForSpecialPlayback(customUrl);
  const s3CdnUrl = convertUrlfromS3toCloudFront(customUrl);
  const logictryPdfJsViewerUrl = isPDF
    ? `https://cdn.logictry.com/js/pdfjs/web/viewer.html?file=${encodeURIComponent(s3CdnUrl)}`
    : null;
  const isUserUploadVideoPlaybackUrl = isLogictryUserUploadVideoPlaybackUrl(customUrl, !!isS3Url);
  const logictryUserUploadVideoIframeUrl = isUserUploadVideoPlaybackUrl
    ? logictryUploadedVideoIframeUrl(customUrl)
    : null;
  return {
    otherAcceptedUrls,
    isS3Url,
    isImage,
    googleUrl,
    googleDriveUrl,
    youtubeUrl,
    loomUrl,
    airtableUrl,
    googleMapsUrl,
    canvaUrl,
    googleCalendarUrl,
    vimeoUrl,
    instagramEmbedCode,
    tiktokEmbedCode,
    isPDF,
    isLogictryUrl,
    videoUrl,
    s3CdnUrl,
    logictryPdfJsViewerUrl,
    isUserUploadVideoPlaybackUrl,
    logictryUserUploadVideoIframeUrl,
  };
}

/** Same copy as existingContent in CreateTemplatePopup */
export const CONTENT_SOURCE_UNSUPPORTED_MESSAGE = 'This content source is not supported. Please use a URL from one of the following supported sources: PDF, Google Drive, YouTube, Loom, Airtable, Google Maps, Canva, Google Calendar, Vimeo, Instagram, or TikTok. If you would like us to support an additional source, please reach out.';

/**
 * Same rules as saving Tree custom source / Create Content ("existing"): checkContentType + Logictry-domain rejection.
 * @param rawUrl Untrimmed URL or empty string
 * @param {{ required?: boolean, emptyOk?: boolean }} opts `required`: non-empty mandated; `emptyOk` (default when not required): empty string succeeds
 */
export function validateContentSourceUrl(rawUrl, opts = {}) {
  const required = !!opts.required;
  const emptyOk = opts.emptyOk !== false && !required;
  const trimmed = typeof rawUrl === 'string' ? rawUrl.trim() : '';

  if (!trimmed) {
    if (required) return { ok: false, error: 'Content URL is missing' };
    if (emptyOk) return { ok: true, absoluteUrl: '' };
    return { ok: false, error: 'Content URL is missing' };
  }

  const absoluteUrl = isUrlValid(trimmed);
  if (!absoluteUrl) return { ok: false, error: 'Content URL is invalid' };

  const {
    isS3Url,
    isImage,
    googleUrl,
    googleDriveUrl,
    youtubeUrl,
    loomUrl,
    airtableUrl,
    googleMapsUrl,
    canvaUrl,
    googleCalendarUrl,
    vimeoUrl,
    instagramEmbedCode,
    tiktokEmbedCode,
    isPDF,
    isLogictryUrl,
    videoUrl,
    isUserUploadVideoPlaybackUrl,
  } = checkContentType(absoluteUrl);

  if (isLogictryUrl) return { ok: false, error: 'Content URL cannot be a Logictry URL' };

  const accepted =
    isS3Url
    || isImage
    || googleUrl
    || googleDriveUrl
    || youtubeUrl
    || loomUrl
    || airtableUrl
    || googleMapsUrl
    || canvaUrl
    || googleCalendarUrl
    || vimeoUrl
    || instagramEmbedCode
    || tiktokEmbedCode
    || isPDF
    || videoUrl
    || isUserUploadVideoPlaybackUrl;

  if (!accepted) return { ok: false, error: CONTENT_SOURCE_UNSUPPORTED_MESSAGE };

  return { ok: true, absoluteUrl };
}

const LOGICTRY_S3_BUCKET_PREFIXES = [
  'logictryuserfiles',
  'logictrydevuserfiles',
  'logictrydevcompanyfiles',
  'logictrycompanyfiles',
];

/** Legacy path-style / old virtual host: bucket.s3.amazonaws.com */
function isLogictryS3LegacyHostname(hostname) {
  const h = hostname.toLowerCase();
  return ['logictryuserfiles.s3.amazonaws.com', 'logictrydevuserfiles.s3.amazonaws.com', 'logictrydevcompanyfiles.s3.amazonaws.com', 'logictrycompanyfiles.s3.amazonaws.com'].includes(h);
}

/** Virtual-hosted regional endpoints: bucket.s3.<region>.amazonaws.com */
function isLogictryS3RegionalHostname(hostname) {
  const h = hostname.toLowerCase();
  return LOGICTRY_S3_BUCKET_PREFIXES.some((prefix) => {
    const re = new RegExp(`^${prefix}\\.s3\\.[a-z0-9-]+\\.amazonaws\\.com$`);
    return re.test(h);
  });
}

/** True for logictry user/company file buckets (legacy or regional virtual-host). Used e.g. widget referrer allowlist. */
export function isLogictryUserOrCompanyFileS3Hostname(hostname) {
  if (!hostname || typeof hostname !== 'string') return false;
  return isLogictryS3LegacyHostname(hostname) || isLogictryS3RegionalHostname(hostname);
}

/** `logictryuserfiles` S3 virtual host only (not dev/company buckets). */
export function isLogictryUserfilesS3Hostname(hostname) {
  if (!hostname || typeof hostname !== 'string') return false;
  const h = hostname.toLowerCase();
  if (h === 'logictryuserfiles.s3.amazonaws.com') return true;
  return /^logictryuserfiles\.s3\.[a-z0-9-]+\.amazonaws\.com$/.test(h);
}

function validateS3Url(url) {
  try {
    const urlParts = new URL(url);
    const { hostname } = urlParts;
    if (isLogictryS3LegacyHostname(hostname) || isLogictryS3RegionalHostname(hostname)) {
      return url;
    }
    return null;
  } catch {
    return null;
  }
}

/** `logictryuserfiles` S3 entry URL for an HTML bundle (custom app iframe on native). */
export function isS3WebAppEntryUrl(url) {
  if (!validateS3Url(url)) return false;
  try {
    const u = new URL(url);
    if (!isLogictryUserfilesS3Hostname(u.hostname)) return false;
    const pathname = u.pathname.toLowerCase();
    return pathname.endsWith('.html') || pathname.endsWith('.htm') || pathname.endsWith('/');
  } catch {
    return false;
  }
}
function validateLogictryUrl(url) {
  try {
    // Parse the URL
    const urlParts = new URL(url);

    // Ensure it's a Google Docs URL
    if (!urlParts.hostname.includes("logictry.com") || ['cdn.logictry.com'].find((u) => urlParts.hostname.includes(u))) {
      return null
    }
    return url;
  } catch {
    return null;
  }
}
function validateVideoUrl(url) {
  try {
    // Parse the URL
    const urlParts = new URL(url);

    // Ensure it's a Google Docs URL
    if (!urlParts.pathname.includes(".m3u8")) {
      return null
    }
    return url;
  } catch {
    return null;
  }
}
function convertGoogleDriveUrl(url) {
  try {
    const urlParts = new URL(url);

    const hostname = urlParts.hostname;
    const pathname = urlParts.pathname;

    if (hostname.includes("drive.google.com")) {
      if (pathname.includes("/drive/folders/")) {
        const regex = /\/folders\/([a-zA-Z0-9_-]+)/;
        const match = pathname.match(regex);
        if (match && match[1]) {
          const folderId = match[1];
          return `https://drive.google.com/embeddedfolderview?id=${folderId}#grid`;
        }
      }

      if (pathname.includes("/file/d/")) {
        throw new Error("This is a file, not a folder. Cannot embed.");
      }
    }
    throw new Error("Unsupported Google Docs URL");
  } catch {
    return null;
  }
}
function convertToGooglePreviewOrMobileBasicUrl(url) {
  try {
    const urlParts = new URL(url);
    const hostname = urlParts.hostname;
    const pathname = urlParts.pathname;
    const queryParams = urlParts.search;
    const hashFragment = urlParts.hash;

    const isDocsHost = hostname.includes("docs.google.com");
    const isDriveHost = hostname.includes("drive.google.com");

    if (!isDocsHost && !isDriveHost) {
      throw new Error("URL is not a supported Google Docs or Drive URL");
    }

    if (pathname.includes("/d/e/")) {
      return url;
    }

    const regex = /\/d\/([a-zA-Z0-9-_]+)/;
    const match = url.match(regex);
    if (!match || match.length < 2) {
      throw new Error("Invalid URL format");
    }

    const id = match[1];

    if (isDriveHost && pathname.includes("/file/")) {
      return `https://drive.google.com/file/d/${id}/preview${queryParams}${hashFragment}`;
    }

    const isSpreadsheet = pathname.includes("/spreadsheets/");
    const isDocument = pathname.includes("/document/");

    if (isSpreadsheet) {
      return `https://docs.google.com/spreadsheets/d/${id}/preview${queryParams}${hashFragment}`;
    } else if (isDocument) {
      return `https://docs.google.com/document/d/${id}/preview${queryParams}${hashFragment}`;
    }

    throw new Error("Unsupported Google Docs path");
  } catch {
    return null;
  }
}
export function convertToYouTubeEmbedUrl(url) {
  try {
    // Parse the URL
    const urlParts = new URL(url);

    // Validate it's a YouTube URL
    if (!urlParts.hostname.includes("youtube.com") && !urlParts.hostname.includes("youtu.be")) {
      throw new Error("Invalid YouTube URL");
    }

    let videoId;

    // Handle different URL formats
    if (urlParts.hostname === "youtu.be") {
      videoId = urlParts.pathname.slice(1);
    } else if (urlParts.pathname.startsWith("/watch") && urlParts.searchParams.has("v")) {
      videoId = urlParts.searchParams.get("v");
    } else if (urlParts.pathname.startsWith("/embed/") || urlParts.pathname.startsWith("/shorts/") || urlParts.pathname.startsWith("/live/")) {
      videoId = urlParts.pathname.split("/")[2];
    } else {
      throw new Error("No valid video ID found in the URL");
    }

    // Construct the embed URL
    const embedUrl = new URL(`https://www.youtube.com/embed/${videoId}`);

    // Keep timestamp; drop playlist/watch params that would change which video plays.
    const skip = new Set(['v', 'list', 'index', 'pp', 'feature', 'ab_channel', 'si']);
    urlParts.searchParams.forEach((value, key) => {
      if (skip.has(key)) return;
      embedUrl.searchParams.append(key, value);
    });

    return embedUrl.toString();
  } catch {
    return null; // Return null if the URL is invalid
  }
}

export function youtubeVideoIdFromUrl(url) {
  const embed = convertToYouTubeEmbedUrl(url);
  if (!embed) return null;
  try {
    const id = new URL(embed).pathname.split('/').filter(Boolean)[1];
    return id && /^[\w-]{11}$/.test(id) ? id : null;
  } catch {
    return null;
  }
}

/** YouTube still used as a landing poster until the content OG image loads. */
export function youtubePosterUrl(url) {
  const id = youtubeVideoIdFromUrl(url);
  return id ? `https://i.ytimg.com/vi/${id}/maxresdefault.jpg` : '';
}

/**
 * YouTube embed URL with livestream playback params.
 */
export function logictryYoutubeEmbedUrl(youtubeUrl, {
  autoplay = false,
  startMuted = false,
} = {}) {
  const embed = convertToYouTubeEmbedUrl(youtubeUrl);
  if (!embed) return null;
  let src;
  try {
    src = new URL(embed);
  } catch {
    return null;
  }
  src.searchParams.set('autoplay', autoplay ? '1' : '0');
  src.searchParams.set('mute', (startMuted || autoplay) ? '1' : '0');
  src.searchParams.set('playsinline', '1');
  src.searchParams.set('rel', '0');
  src.searchParams.set('iv_load_policy', '3');
  src.searchParams.set('controls', '0');
  src.searchParams.set('fs', '1');
  return src.toString();
}

/**
 * YouTube Error 153: embed requires an HTTPS Referer from a real site.
 * That includes Capacitor (`capacitor://`) AND a native WKWebView that
 * loads youtube.com/embed as the top-level page (empty Referer).
 * Wrap through production `/videoplayer` so YouTube sees logictry.com.
 */
export function logictryYoutubeVideoplayerIframeUrl(youtubeUrl, {
  autoplay = false,
  startMuted = false,
} = {}) {
  const embed = logictryYoutubeEmbedUrl(youtubeUrl, { autoplay, startMuted });
  if (!embed) return null;
  return `${PRODUCTION_VIDEO_PLAYER_ORIGIN}/videoplayer?video=${encodeURIComponent(embed)}`;
}
function convertToLoomEmbedUrl(url) {
  try {
    const urlParts = new URL(url);
    if (!urlParts.hostname.includes("loom.com")) {
      throw new Error("Invalid YouTube URL");
    }
    let videoId;
    if (urlParts.pathname.startsWith("/embed/") || urlParts.pathname.startsWith("/share/")) {
      videoId = urlParts.pathname.split("/")[2];
    } else {
      throw new Error("No valid video ID found in the URL");
    }
    const embedUrl = new URL(`https://www.loom.com/embed/${videoId}`);
    return embedUrl.toString();
  } catch {
    return null; // Return null if the URL is invalid
  }
}
function isPdfUrl(url) {
  try {
    // Parse the URL
    const urlParts = new URL(url);

    // Check if the pathname ends with ".pdf" (case-insensitive)
    return urlParts.pathname.toLowerCase().endsWith(".pdf");
  } catch {
    return false; // Return false if the URL is invalid
  }
}
function validateAirtableUrl(url) {
  try {
    const urlParts = new URL(url);

    if (!urlParts.hostname.includes("airtable.com")) {
      throw new Error("Invalid Airtable URL");
    }
    let pathname = urlParts.pathname;
    if (pathname.startsWith("/embed/")) {
      pathname = pathname.split('/embed')[1];
    }

    const embedUrl = new URL(`https://airtable.com/embed${pathname}`);
    return embedUrl.toString();
  } catch {
    return null;
  }
}
function convertToGoogleMapsEmbed(url) {
  try {
    // If the URL is already in the correct embed format with mid
    const embedPattern = /^https:\/\/www\.google\.com\/maps\/d\/embed\?mid=([a-zA-Z0-9-_]+)/;
    if (embedPattern.test(url)) {
      return url; // Return the URL as is if it is already in embed format
    }

    // Check if the URL is a valid Google Maps viewer URL with a mid
    const googleMapsPattern = /^https:\/\/www\.google\.com\/maps\/d\/u\/0\/viewer\?/;
    if (!googleMapsPattern.test(url)) {
      return null; // Not a Google Maps URL with a valid mid
    }

    // Match the mid parameter for a valid embed URL (example: https://www.google.com/maps/d/u/0/viewer?mid=...)
    const midPattern = /[?&]mid=([a-zA-Z0-9-_]+)/;
    const midMatch = url.match(midPattern);

    if (midMatch) {
      const mid = midMatch[1]; // Extract map ID
      return `https://www.google.com/maps/d/embed?mid=${mid}`; // Embed format with mid
    }

    // If no mid is found, return null (it's not a valid embed URL)
    return null;
  } catch {
    return null; // Return null in case of error
  }
}
function isImageUrl(input) {
  if (!input) {
    return false; // Handle null or undefined input
  }

  // Common image file extensions
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'tiff', 'ico', 'avif'];

  // Check if the input contains a MIME type or file extension
  const lowerInput = input.toLowerCase();

  // Check by file extension
  const extensionMatch = lowerInput.match(/\.(\w+)$/);
  if (extensionMatch) {
    const extension = extensionMatch[1]; // Extract the file extension
    if (imageExtensions.includes(extension)) {
      return true;
    }
  }

  // Check by MIME type
  if (lowerInput.startsWith('image/')) {
    return true;
  }

  return false; // Not an image
}
function convertToCanvaEmbedUrl(url) {
  try {
    // Regular expression to match Canva design URLs
    const regex = /https:\/\/www\.canva\.com\/design\/([a-zA-Z0-9-_]+)\/([a-zA-Z0-9-_]+)(?:\/[a-zA-Z]+)?/;
    const match = url.match(regex);

    // Check if the URL matches the expected pattern
    if (match && match.length >= 3) {
      const designId = match[1];
      const viewId = match[2];

      // Return the proper embed URL
      return `https://www.canva.com/design/${designId}/${viewId}/view?embed`;
    }

    // If not a valid Canva design URL, return null
    return null;
  } catch {
    return null;
  }
}
function convertToGoogleCalendarEmbedUrl(url) {
  try {
    // Parse the input URL
    const urlParts = new URL(url);

    // Ensure the URL is a Google Calendar URL
    if (!urlParts.hostname.includes("calendar.google.com")) {
      return null; // Not a Google Calendar URL
    }

    // Extract query parameters
    const params = new URLSearchParams(urlParts.search);

    // Check for required parameters
    const calendarId = params.get("src");
    const timezone = params.get("ctz");

    if (!calendarId) {
      throw new Error("Missing calendar ID (src) in the URL");
    }

    // Construct the embed URL
    const embedUrl = new URL("https://calendar.google.com/calendar/embed");
    embedUrl.searchParams.set("src", calendarId);

    // Set timezone if available
    if (timezone) {
      embedUrl.searchParams.set("ctz", timezone);
    }

    return embedUrl.toString();
  } catch {
    return null;
  }
}
function convertVimeoToEmbed(url) {
  const regex = /(?:https?:\/\/)?(?:www\.|player\.)?vimeo\.com\/(?:video\/)?(\d+)(?:\/(\w+))?/;
  const match = url.match(regex);
  if (match && match[1]) {
    let embedUrl = `https://player.vimeo.com/video/${match[1]}`;
    // If a hash parameter (h=) is found, append it to the URL
    if (match[2]) {
      embedUrl += `?h=${match[2]}`;
    }
    return embedUrl;
  }
  return null;
}
function convertInstagramReelToEmbedUrl(url) {
  const regex = /(?:https?:\/\/)?(?:www\.)?instagram\.com\/(reel|p)\/([A-Za-z0-9_-]+)/;
  const match = url.match(regex);

  if (match && match[2]) {
    return `https://www.instagram.com/${match[1]}/${match[2]}/embed/captioned`;
  }

  return null;
}

function convertTikTokToEmbedUrl(url) {
  const regex = /(?:https?:\/\/)?(?:www\.)?tiktok\.com\/(?:@[\w.-]+\/video\/(\d+))/;
  const match = url.match(regex);
  if (match && match[1]) {
    return `https://www.tiktok.com/embed/${match[1]}`;
  }
  return null; // Returns null if the URL doesn't match the pattern
}
function otherAcceptedSitesForSpecialPlayback(url) {
  try {
    const parsed = new URL(url);

    const allowedHosts = [
      'cdnapisec.kaltura.com',
      // add more here
      // 'player.vimeo.com',
      // 'www.youtube.com',
    ];

    return allowedHosts.includes(parsed.hostname);
  } catch {
    return false; // invalid URL
  }
}
